import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import {
  LogIn, LogOut, ArrowLeft, Mail, Phone, MapPin, Calendar, Trash2,
  RefreshCw, ImageIcon, ShieldAlert, ChevronRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO = "https://customer-assets.emergentagent.com/job_maid-service-6/artifacts/elfl7tug_F736BDB4-A6C3-463A-9B02-CDF53E55EE0B.png";

const STATUS_LABELS = {
  new: "Nouvelle",
  contacted: "Contacté",
  done: "Terminé",
  archived: "Archivé",
};
const STATUS_COLORS = {
  new: "bg-[#C8A258] text-[#0B0A08]",
  contacted: "bg-blue-500/20 text-blue-300 border border-blue-500/40",
  done: "bg-green-500/20 text-green-300 border border-green-500/40",
  archived: "bg-[#EDE3CF]/10 text-[#EDE3CF]/60 border border-[#EDE3CF]/20",
};

function LoginScreen({ failed }) {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#0B0A08" }}>
      <div className="max-w-md w-full bg-[#15130E] border border-[#C8A258]/20 rounded-sm p-10 text-center">
        <img src={LOGO} alt="AGL" className="h-20 w-20 object-contain mx-auto mb-6" />
        <div className="text-xs tracking-[0.3em] uppercase text-[#C8A258] mb-3">Espace administrateur</div>
        <h1 className="font-display text-3xl text-[#EDE3CF] mb-3">
          AGL <span className="text-[#C8A258]">Bois Rond</span>
        </h1>
        <p className="text-[#EDE3CF]/65 text-sm mb-8 leading-relaxed">
          Cet espace est réservé à l'administrateur. Connectez-vous avec le compte Google
          autorisé pour consulter les soumissions reçues.
        </p>
        {failed && (
          <div className="mb-6 px-4 py-3 bg-red-900/30 border border-red-500/30 rounded-sm text-red-200 text-sm flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0" />
            Authentification échouée. Réessayez.
          </div>
        )}
        <Button onClick={handleLogin}
          className="w-full rounded-sm bg-[#C8A258] hover:bg-[#B0894A] text-[#0B0A08] h-12 text-base"
          data-testid="admin-login-btn">
          <LogIn className="w-4 h-4 mr-2" /> Se connecter avec Google
        </Button>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-[#EDE3CF]/55 hover:text-[#C8A258]" data-testid="admin-back-home">
          <ArrowLeft className="w-4 h-4" /> Retour au site
        </Link>
      </div>
    </div>
  );
}

function NotAuthorized({ email, onLogout }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#0B0A08" }}>
      <div className="max-w-md w-full bg-[#15130E] border border-red-500/30 rounded-sm p-10 text-center">
        <ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h1 className="font-display text-2xl text-[#EDE3CF] mb-3">Accès refusé</h1>
        <p className="text-[#EDE3CF]/70 text-sm mb-6">
          Le compte <strong className="text-[#C8A258]">{email}</strong> n'est pas autorisé à
          accéder au tableau de bord. Connectez-vous avec un compte administrateur.
        </p>
        <Button onClick={onLogout} className="rounded-sm bg-[#C8A258] hover:bg-[#B0894A] text-[#0B0A08]" data-testid="admin-logout-unauth">
          <LogOut className="w-4 h-4 mr-2" /> Déconnexion
        </Button>
      </div>
    </div>
  );
}

function QuoteRow({ q, onUpdate, onDelete, onSelect, selected }) {
  return (
    <button
      onClick={() => onSelect(q)}
      className={`w-full text-left p-5 border-b border-[#C8A258]/10 transition-colors ${selected ? "bg-[#15130E]" : "hover:bg-[#15130E]/60"}`}
      data-testid={`admin-quote-row-${q.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="font-display text-lg text-[#EDE3CF] truncate">{q.name}</div>
        <span className={`px-2 py-0.5 text-[10px] tracking-wider uppercase rounded-sm shrink-0 ${STATUS_COLORS[q.status] || STATUS_COLORS.new}`}>
          {STATUS_LABELS[q.status] || "Nouvelle"}
        </span>
      </div>
      <div className="text-xs text-[#EDE3CF]/55 mb-2">{q.service_type}</div>
      <div className="flex items-center justify-between text-xs text-[#EDE3CF]/60">
        <span className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-[#C8A258]" /> {q.phone}</span>
        <span className="flex items-center gap-1">
          {q.photo_urls?.length ? <><ImageIcon className="w-3 h-3 text-[#C8A258]" /> {q.photo_urls.length}</> : null}
          <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </span>
      </div>
    </button>
  );
}

function QuoteDetail({ q, onUpdate, onDelete, onClose }) {
  if (!q) return null;
  const date = new Date(q.created_at).toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" });
  return (
    <div className="bg-[#15130E] border border-[#C8A258]/15 rounded-sm p-6 md:p-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-[#C8A258] mb-1">{q.service_type}</div>
          <h2 className="font-display text-3xl text-[#EDE3CF]">{q.name}</h2>
          <div className="text-xs text-[#EDE3CF]/55 mt-1 flex items-center gap-2">
            <Calendar className="w-3 h-3" /> {date}
          </div>
        </div>
        <Select value={q.status || "new"} onValueChange={(v) => onUpdate(q.id, v)}>
          <SelectTrigger className="w-[150px] rounded-sm bg-[#0B0A08] border-[#C8A258]/30 text-[#EDE3CF] h-9" data-testid={`admin-status-select-${q.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm bg-[#15130E] border-[#C8A258]/30 text-[#EDE3CF]">
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <a href={`tel:${q.phone}`} className="flex items-center gap-3 px-4 py-3 bg-[#0B0A08] border border-[#C8A258]/15 rounded-sm hover:border-[#C8A258]/40 text-sm">
          <Phone className="w-4 h-4 text-[#C8A258]" /> <span className="text-[#EDE3CF]">{q.phone}</span>
        </a>
        <a href={`mailto:${q.email}`} className="flex items-center gap-3 px-4 py-3 bg-[#0B0A08] border border-[#C8A258]/15 rounded-sm hover:border-[#C8A258]/40 text-sm break-all">
          <Mail className="w-4 h-4 text-[#C8A258] shrink-0" /> <span className="text-[#EDE3CF]">{q.email}</span>
        </a>
        {q.address ? (
          <div className="sm:col-span-2 flex items-center gap-3 px-4 py-3 bg-[#0B0A08] border border-[#C8A258]/15 rounded-sm text-sm">
            <MapPin className="w-4 h-4 text-[#C8A258]" /> <span className="text-[#EDE3CF]">{q.address}</span>
          </div>
        ) : null}
      </div>

      {q.message ? (
        <div className="mb-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C8A258] mb-2">Message</div>
          <p className="text-[#EDE3CF]/85 text-sm leading-relaxed whitespace-pre-wrap bg-[#0B0A08] border border-[#C8A258]/10 p-4 rounded-sm">
            {q.message}
          </p>
        </div>
      ) : null}

      {q.photo_urls?.length > 0 && (
        <div className="mb-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C8A258] mb-3">
            Photos ({q.photo_urls.length})
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {q.photo_urls.map((u, i) => {
              const fullUrl = u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
              return (
                <a key={i} href={fullUrl} target="_blank" rel="noreferrer"
                  className="aspect-square rounded-sm overflow-hidden border border-[#C8A258]/20 hover:border-[#C8A258]/60 transition-colors block">
                  <img src={fullUrl} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                </a>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-5 border-t border-[#C8A258]/10">
        <Button variant="outline" onClick={onClose}
          className="rounded-sm border-[#C8A258]/30 bg-transparent text-[#EDE3CF] hover:bg-[#C8A258]/10">
          Fermer
        </Button>
        <Button variant="outline" onClick={() => {
            if (window.confirm("Supprimer cette demande ? Cette action est irréversible.")) onDelete(q.id);
          }}
          className="rounded-sm border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200"
          data-testid={`admin-delete-${q.id}`}>
          <Trash2 className="w-4 h-4 mr-2" /> Supprimer
        </Button>
      </div>
    </div>
  );
}

export default function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const authFailed = params.get("auth") === "failed";

  const [authState, setAuthState] = useState(location.state?.user ? "ok" : "checking"); // checking | ok | none | unauth
  const [user, setUser] = useState(location.state?.user || null);
  const [quotes, setQuotes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  // Initial auth check
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // AuthCallback handles this
    if (location.state?.user) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
        setAuthState(data.is_admin ? "ok" : "unauth");
      } catch {
        setAuthState("none");
      }
    })();
    // eslint-disable-next-line
  }, []);

  // After login, check is_admin
  useEffect(() => {
    if (user && !user.is_admin) setAuthState("unauth");
    else if (user?.is_admin) setAuthState("ok");
  }, [user]);

  // Fetch quotes when authorized
  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, { withCredentials: true });
      setQuotes(data);
    } catch (e) {
      if (e.response?.status === 401) setAuthState("none");
      else if (e.response?.status === 403) setAuthState("unauth");
      else toast.error("Impossible de charger les soumissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authState === "ok") fetchQuotes();
    // eslint-disable-next-line
  }, [authState]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    setUser(null);
    setAuthState("none");
    navigate("/admin", { replace: true });
  };

  const handleUpdate = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, { withCredentials: true });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success(`Statut : ${STATUS_LABELS[status]}`);
    } catch {
      toast.error("Mise à jour échouée");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, { withCredentials: true });
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Demande supprimée");
    } catch {
      toast.error("Suppression échouée");
    }
  };

  if (authState === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0B0A08" }}>
        <div className="w-10 h-10 border-2 border-[#C8A258]/30 border-t-[#C8A258] rounded-full animate-spin" />
      </div>
    );
  }
  if (authState === "none") return <><Toaster theme="dark" position="top-center" richColors /><LoginScreen failed={authFailed} /></>;
  if (authState === "unauth") return <><Toaster theme="dark" position="top-center" richColors /><NotAuthorized email={user?.email} onLogout={handleLogout} /></>;

  const filteredQuotes = filter === "all" ? quotes : quotes.filter((q) => (q.status || "new") === filter);
  const counts = quotes.reduce((acc, q) => { const s = q.status || "new"; acc[s] = (acc[s] || 0) + 1; return acc; }, {});

  return (
    <div className="min-h-screen" style={{ background: "#0B0A08" }} data-testid="admin-dashboard">
      <Toaster theme="dark" position="top-center" richColors />
      <header className="border-b border-[#C8A258]/15 sticky top-0 z-40 backdrop-blur-xl"
        style={{ background: "rgba(11,10,8,0.85)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img src={LOGO} alt="AGL" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display text-lg text-[#EDE3CF]">
                Tableau de bord <span className="text-[#C8A258]">Admin</span>
              </div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#C8A258]/80">AGL Bois Rond Inc.</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-3 px-3 py-1.5 bg-[#15130E] border border-[#C8A258]/20 rounded-sm">
              {user?.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : null}
              <span className="text-xs text-[#EDE3CF]/80">{user?.email}</span>
            </div>
            <Button variant="outline" onClick={fetchQuotes} disabled={loading}
              className="rounded-sm border-[#C8A258]/30 bg-transparent text-[#EDE3CF] hover:bg-[#C8A258]/10" data-testid="admin-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="outline" onClick={handleLogout}
              className="rounded-sm border-[#C8A258]/30 bg-transparent text-[#EDE3CF] hover:bg-[#C8A258]/10" data-testid="admin-logout">
              <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-10">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          {[
            { k: "all", l: `Toutes (${quotes.length})` },
            { k: "new", l: `Nouvelles (${counts.new || 0})` },
            { k: "contacted", l: `Contactées (${counts.contacted || 0})` },
            { k: "done", l: `Terminées (${counts.done || 0})` },
            { k: "archived", l: `Archivées (${counts.archived || 0})` },
          ].map((t) => (
            <button key={t.k} onClick={() => setFilter(t.k)}
              className={`px-4 py-2 rounded-sm text-xs tracking-wider uppercase transition-colors ${
                filter === t.k
                  ? "bg-[#C8A258] text-[#0B0A08]"
                  : "bg-[#15130E] text-[#EDE3CF]/70 border border-[#C8A258]/20 hover:text-[#C8A258]"
              }`}
              data-testid={`admin-filter-${t.k}`}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-5 bg-[#15130E] border border-[#C8A258]/15 rounded-sm overflow-hidden max-h-[75vh] overflow-y-auto" data-testid="admin-quotes-list">
            {filteredQuotes.length === 0 ? (
              <div className="p-10 text-center text-[#EDE3CF]/55 text-sm">
                {loading ? "Chargement..." : "Aucune demande dans cette catégorie."}
              </div>
            ) : (
              filteredQuotes.map((q) => (
                <QuoteRow key={q.id} q={q} onUpdate={handleUpdate} onDelete={handleDelete}
                  onSelect={setSelected} selected={selected?.id === q.id} />
              ))
            )}
          </aside>
          <section className="lg:col-span-7">
            {selected ? (
              <QuoteDetail q={selected} onUpdate={handleUpdate} onDelete={handleDelete} onClose={() => setSelected(null)} />
            ) : (
              <div className="bg-[#15130E] border border-dashed border-[#C8A258]/20 rounded-sm p-16 text-center">
                <div className="font-display text-xl text-[#EDE3CF]/55 mb-2">Sélectionnez une demande</div>
                <p className="text-sm text-[#EDE3CF]/45">Cliquez sur une soumission à gauche pour voir les détails.</p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
